/**
 * What the brain autopilot has actually done, batch by batch.
 *
 * The scanner's history next door counts sites; this one counts money. The last
 * column is browser seconds rather than a fourth tally, because "the batch did
 * three runs" and "the batch spent 54 seconds of a 420-second daily allowance"
 * are different facts and only the second one predicts whether the button will
 * work this afternoon.
 */
import { AlertTriangle } from "lucide-react";
import { formatWhen } from "../../../../lib/siteScan";
import type { BrainAutopilotRun } from "../../../../lib/siteBrain";
import { StatusPill, fmtDateTime, fmtNum, listCard } from "../../../ui";
import type { T } from "../../site-scanner/types";

/** Started, trigger, started runs, done, failed, browser seconds. */
const COLS = "1.1fr 1fr .7fr .7fr .7fr .8fr";

/** `browser-budget` -> `browserBudget`, so a server enum can address a key. */
const noteKey = (note: string): string => note.replace(/-([a-z])/g, (_, c: string) => c.toUpperCase());

export function BrainAutopilotHistory({ runs, t }: { runs: BrainAutopilotRun[]; t: T }) {
  if (!runs.length) return null;

  return (
    // `crm-list-scroll` for the reason the scanner's log carries it: this list
    // is a guest in the settings panel, and without its own scroller the panel
    // takes the list's floor width and a swipe on the log drags all of it.
    <section className="border-t border-[var(--crm-hair)] pt-4">
      <h3 className="crm-card-t px-[26px] pb-2 text-[15px]">{t("leads.brainAuto.history")}</h3>
      <div className="crm-list-scroll pb-2">
        <div className="crm-list-head" style={{ ...listCard(COLS), gridTemplateColumns: COLS, paddingTop: 0 }}>
          <span>{t("leads.brainAuto.run.started")}</span>
          <span>{t("leads.brainAuto.run.trigger")}</span>
          <span>{t("leads.brainAuto.run.picked")}</span>
          <span>{t("leads.brainAuto.run.done")}</span>
          <span>{t("leads.brainAuto.run.failed")}</span>
          <span>{t("leads.brainAuto.run.browser")}</span>
        </div>

        <div className="crm-rows pt-0" style={listCard(COLS)}>
          {runs.map((run) => (
            <div key={run.id} className="crm-list-row" style={{ gridTemplateColumns: COLS }}>
              <span
                className="crm-tnum flex min-w-0 items-center gap-2 pl-3 text-[12.5px]"
                title={fmtDateTime(run.startedAt)}
              >
                {run.error ? <AlertTriangle className="size-3.5 shrink-0 text-[var(--color-destructive)]" /> : null}
                <span className="crm-trunc">{formatWhen(run.startedAt)}</span>
              </span>
              <span>
                <StatusPill tone={run.trigger === "manual" ? "info" : "mut"}>
                  {t(`leads.brainAuto.trigger.${run.trigger}`)}
                </StatusPill>
              </span>

              {/* A batch that had nothing to do says so in words. Four zeros is
                  the same picture a broken engine draws, and this engine has an
                  extra way of doing nothing that is entirely normal - it ran out
                  of browser time, which happens most days by design. */}
              {run.note ? (
                <span className="crm-mut crm-trunc text-[12.5px]" style={{ gridColumn: "3 / -1" }}>
                  {t(`leads.brainAuto.note.${noteKey(run.note)}`)}
                </span>
              ) : (
                <>
                  <span className="crm-tnum text-[13px]">{fmtNum(run.picked)}</span>
                  {/* Done is what the batch exists to produce, so it carries the
                      weight; the rest is context. */}
                  <span className="crm-tnum text-[13.25px] font-bold">{fmtNum(run.done)}</span>
                  <span
                    className={
                      run.failed > 0
                        ? "crm-tnum text-[13px] font-bold text-[var(--color-destructive)]"
                        : "crm-mut crm-tnum text-[13px]"
                    }
                  >
                    {fmtNum(run.failed)}
                  </span>
                  <span className="crm-mut crm-tnum text-[13px]">
                    {t("leads.brainAuto.run.seconds", { n: Math.round(run.browserMs / 1000) })}
                  </span>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
